if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        const formData = new FormData(this.form);
        const giftAddonsPayload = this.getGiftAddonsPayload(formData);

        if (giftAddonsPayload?.error) {
          this.handleErrorMessage(giftAddonsPayload.error);
          if (giftAddonsPayload.element && typeof giftAddonsPayload.element.focus === 'function') {
            giftAddonsPayload.element.focus();
          }
          return;
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        if (this.cart) {
          this.cart.setActiveElement(document.activeElement);
        }

        if (giftAddonsPayload?.items) {
          config.body = JSON.stringify({
            items: giftAddonsPayload.items,
            sections: this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : [],
            sections_url: window.location.pathname,
          });
        } else {
          delete config.headers['Content-Type'];
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
          }
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(window.cartStrings?.error || '');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      getGiftAddonsPayload(formData) {
        const addonScope = this.closest('product-info') || this.form;
        const addons = addonScope.querySelector('[data-gift-addons]');
        const personalizationAddons = addonScope.querySelector('[data-personalization-addons]');
        if (!addons && !personalizationAddons) return null;

        const giftWrap = addons ? this.getGiftAddonSelection(addons, 'gift_wrap') : null;
        const giftCard = addons ? this.getGiftAddonSelection(addons, 'gift_card') : null;
        const personalizationValidation =
          typeof personalizationAddons?.validate === 'function' ? personalizationAddons.validate() : null;

        if (personalizationValidation?.error) {
          return personalizationValidation;
        }

        const personalizationSelections =
          typeof personalizationAddons?.getSelections === 'function' ? personalizationAddons.getSelections() : [];

        if (!giftWrap && !giftCard && personalizationSelections.length === 0) {
          if (addons) this.setGiftMessageError(addons, '');
          if (typeof personalizationAddons?.clearErrors === 'function') personalizationAddons.clearErrors();
          return null;
        }

        const mainVariantId = Number(formData.get('id'));
        const quantity = Math.max(Number(formData.get('quantity') || 1), 1);

        if (!mainVariantId) {
          return { error: addons?.dataset.addError || personalizationAddons?.dataset.addError || window.cartStrings.error };
        }

        if (giftCard && addons) {
          const message = giftCard.message.trim();

          if (!message) {
            const error = addons.dataset.messageError || '';
            this.setGiftMessageError(addons, error);
            return { error, element: giftCard.messageInput };
          }

          this.setGiftMessageError(addons, '');
        }

        const groupId = Date.now().toString(36) + Math.random().toString(36).slice(2);
        const mainProperties = this.getMainProperties(formData);
        mainProperties._issi_group_id = groupId;

        const mainItem = {
          id: mainVariantId,
          quantity,
          properties: mainProperties,
        };
        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) mainItem.selling_plan = sellingPlan;

        const addonDataset = addons?.dataset || personalizationAddons?.dataset || {};
        const parentProperties = {
          _issi_addon: 'true',
          _issi_parent_variant_id: String(mainVariantId),
          _issi_parent_product_handle: addonDataset.productHandle || '',
          _issi_parent_product_title: addonDataset.productTitle || '',
          _issi_group_id: groupId,
        };
        const items = [mainItem];

        if (giftWrap) {
          items.push({
            id: giftWrap.variantId,
            quantity,
            properties: {
              ...parentProperties,
              _issi_addon_type: 'gift_wrap',
            },
          });
        }

        if (giftCard) {
          items.push({
            id: giftCard.variantId,
            quantity: 1,
            properties: {
              [addons.dataset.messagePropertyName || 'Gift message']: giftCard.message.trim(),
              ...parentProperties,
              _issi_addon_type: 'gift_card',
            },
          });
        }

        personalizationSelections.forEach((selection) => {
          items.push({
            id: selection.variantId,
            quantity,
            properties: {
              [personalizationAddons.dataset.sidePropertyName || 'Side']: selection.sideLabel,
              [personalizationAddons.dataset.textPropertyName || 'Text']: selection.text,
              [personalizationAddons.dataset.fontPropertyName || 'Font']: selection.fontLabel || selection.font,
              ...parentProperties,
              _issi_addon_type: selection.addonType,
            },
          });
        });

        return { items };
      }

      getGiftAddonSelection(addons, type) {
        const item = addons.querySelector(`[data-addon-type="${type}"]`);
        if (!item || !item.querySelector('[data-addon-checkbox]')?.checked) return null;

        const variant = item.querySelector('[data-addon-variant]');
        const variantId = Number(variant?.value);
        if (!variantId) return null;

        const modal = typeof addons.getGiftCardModal === 'function' ? addons.getGiftCardModal(item) : item.querySelector('[data-gift-card-modal]');
        const messageInput = item.querySelector('[data-gift-message]') || modal?.querySelector('[data-gift-message]');

        return {
          variantId,
          messageInput,
          message: messageInput?.value || '',
        };
      }

      getMainProperties(formData) {
        const properties = {};

        formData.forEach((value, key) => {
          const match = key.match(/^properties\[(.+)\]$/);
          if (!match || value === '') return;
          properties[match[1]] = value;
        });

        return properties;
      }

      setGiftMessageError(addons, message) {
        const giftCardItem = addons.querySelector('[data-addon-type="gift_card"]');
        const modal = typeof addons.getGiftCardModal === 'function' ? addons.getGiftCardModal(giftCardItem) : giftCardItem?.querySelector('[data-gift-card-modal]');
        const error =
          giftCardItem?.querySelector('[data-gift-message-error]') || modal?.querySelector('[data-gift-message-error]');
        const messageInput =
          giftCardItem?.querySelector('[data-gift-message]') || modal?.querySelector('[data-gift-message]');

        if (messageInput) {
          messageInput.toggleAttribute('aria-invalid', Boolean(message));
        }

        if (message && typeof addons.openGiftCardModal === 'function') {
          if (giftCardItem && modal) {
            addons.openGiftCardModal(giftCardItem);
          }
        }

        if (!error) return;
        error.textContent = message;
        error.hidden = !message;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
